import {
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@aiwyn/shared';

/**
 * Flow 6 — Making a Payment.
 * The user selects invoices, picks a payment method, runs the charge through the
 * payment platform (Stripe), and receives confirmation.
 *
 * Triad shape: the make-payment async leg follows SUBMITTED + SUCCEEDED + FAILED; the 3DS
 * platform-confirmation sub-step follows SUBMITTED + COMPLETE + FAILED. Terminals carry
 * `operationDurationMs`; FAILED metadata extends `FailureContext`. The sync validation gate
 * fires a single `PAYMENT_VALIDATION_FAILED` (no SUBMITTED, no duration). User-driven cancels
 * use the dedicated `PAYMENT_CANCELLED` event — not a failure.
 */
export enum PaymentWorkflowEvent {
  PAYMENT_SETUP_STARTED = 'PAYMENT_SETUP_STARTED',
  PAYMENT_PAYABLES_SELECTED = 'PAYMENT_PAYABLES_SELECTED',
  // PP-3775: the user signaled intent to make a non-invoice (pay-without-invoice) payment by toggling
  // to non-invoice mode or editing the free-form amount. This is the intent-based funnel start for the
  // non-invoice flow, which — unlike the invoice flow — has no auto-selection that could otherwise fire
  // a spurious PAYMENT_PAYABLES_SELECTED on load.
  PAYMENT_NON_INVOICE_STARTED = 'PAYMENT_NON_INVOICE_STARTED',
  // Email-first checkout — the payer entered a valid email and the payment methods unlocked. Fires once
  // per session when the gate flips to unlocked. Lets us measure the funnel impact of requiring an email
  // before the methods. No method is selected yet, so paymentMethodType is undefined on this event.
  PAYMENT_METHODS_UNLOCKED = 'PAYMENT_METHODS_UNLOCKED',
  PAYMENT_METHOD_SELECTED = 'PAYMENT_METHOD_SELECTED',
  // FIRM-10915 (PP-3635): single user event for confirming the engagement Payment Information step.
  // Non-triad: the immediate-vs-deferred backend reality is described by boolean metadata
  // (`prepaymentCharged: false` ⇒ charge deferred behind outstanding signatures ⇒ Waiting). The async
  // charge terminal is a backend event (ART-8104 prepayment.*.async), never emitted client-side.
  PAYMENT_INFO_CONFIRMED = 'PAYMENT_INFO_CONFIRMED',
  PAYMENT_VALIDATION_FAILED = 'PAYMENT_VALIDATION_FAILED',
  PAYMENT_CHARGE_SUBMITTED = 'PAYMENT_CHARGE_SUBMITTED',
  PAYMENT_CHARGE_SUCCEEDED = 'PAYMENT_CHARGE_SUCCEEDED',
  PAYMENT_CHARGE_FAILED = 'PAYMENT_CHARGE_FAILED',
  PAYMENT_PLATFORM_CONFIRMATION_SUBMITTED = 'PAYMENT_PLATFORM_CONFIRMATION_SUBMITTED',
  PAYMENT_PLATFORM_CONFIRMATION_COMPLETE = 'PAYMENT_PLATFORM_CONFIRMATION_COMPLETE',
  PAYMENT_PLATFORM_CONFIRMATION_FAILED = 'PAYMENT_PLATFORM_CONFIRMATION_FAILED',
  PAYMENT_CANCELLED = 'PAYMENT_CANCELLED',
  PAYMENT_LATE_FEES_SUPPRESSED = 'PAYMENT_LATE_FEES_SUPPRESSED',
  PAYMENT_LATE_FEE_ALLOCATION_CHECK_FAILED = 'PAYMENT_LATE_FEE_ALLOCATION_CHECK_FAILED',
  PAYMENT_CREDITS_SUPPRESSED = 'PAYMENT_CREDITS_SUPPRESSED',
  PAYMENT_CREDIT_ALLOCATION_CHECK_FAILED = 'PAYMENT_CREDIT_ALLOCATION_CHECK_FAILED',
}

/**
 * Common context attached to every PaymentWorkflowEvent. Lets a single Pendo/LogRocket
 * funnel filter by payment flavor, method, currency, and entry surface without joining across events.
 *  - `paymentMethodType` is undefined on events that fire before the user has picked a method
 *    (PAYMENT_SETUP_STARTED at sidebar/open-portal entry, and the unpaid-page rehydration fire of
 *    PAYMENT_PAYABLES_SELECTED, are the realistic cases).
 *  - `paymentBatchId` is a UUID minted when `MakePayment` is dispatched and cleared after the batch
 *    settles. It is only populated on events that fire during the make-payment lifecycle
 *    (CHARGE_SUBMITTED, PLATFORM_CONFIRMATION_*, CHARGE_SUCCEEDED, CHARGE_FAILED, CANCELLED). Use it
 *    to join a charge to its outcome and to count retries (distinct batch IDs per session for the
 *    same `paymentType`).
 */
export type PaymentEventBaseMetadata = {
  paymentType: PaymentType;
  paymentMethodType: string | undefined;
  currency: string;
  surface: PaymentSurface;
  paymentBatchId?: string;
};

/**
 * Whether the charge is anchored to one or more AR transactions ('invoice') or is a free-form amount
 * with no AR transaction backing it ('non-invoice', covering pay-without-invoice and quick-payment-link
 * sessions). Quick-payment-link flavor is captured separately via `surface: 'quick-payment-link'`.
 */
export type PaymentType = 'invoice' | 'non-invoice';

/**
 * Whether a selection / intent event was driven by an explicit user action (`user`) or by the portal
 * auto-selecting on load (`auto`). PP-3775: the portal auto-selects invoices and a saved payment method
 * on load, so a passive page load is otherwise indistinguishable from active intent. Pendo funnels gate
 * their "started" step on `intentTrigger: 'user'`; `auto` events still flow so we can measure how often
 * the auto-selection matched what the user ultimately paid.
 */
export type PaymentIntentTrigger = 'auto' | 'user';

/**
 * Entry surface for the payment flow.
 *
 * Placeholder values — intentionally dead until a fire site is wired:
 *  - `engagement` — reserved for an engagement-driven payment entry. No fire site exists today;
 *    the value is in the union so dashboards and saved Pendo segments can be built ahead of the
 *    rollout. Remove from the union if the engagement entry is dropped from the roadmap.
 *
 * Live values (`invoices`, `review-and-pay`, `pay-invoice-email-invoice`, `pay-invoice-email-collections`,
 * `quick-payment-link`) are emitted from `PaymentsState.resolvePaymentSurface()`. Most are classified purely
 * from `window.location.pathname`, but the two `pay-invoice-email-*` values and `review-and-pay` share the
 * same `/reviewAndPay` URL — the pay-invoice email link (handled by `PayInvoiceAuthGuard`) and the in-portal
 * "Review Invoices" button both land there. They are disambiguated by a persisted entry-source override
 * (`PaymentsState.paymentEntrySource`) set at each entry chokepoint. ART-8293: the suffix on the two
 * `pay-invoice-email-*` values mirrors the backend's `linkSource` ('invoice' | 'collections') so a new-invoice
 * notification email can be isolated from a V1/V2 collections/dunning statement email — both land on this
 * same guard-driven path. Links minted before that backend change carried no `linkSource` and default to the
 * `-invoice` suffix (see `ReviewAndPayComponent`).
 */
export type PaymentSurface =
  | 'invoices'
  | 'review-and-pay'
  | 'pay-invoice-email-invoice'
  | 'pay-invoice-email-collections'
  | 'quick-payment-link'
  | 'engagement';

export type PaymentWorkflowEventMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  PaymentWorkflowEvent,
  {
    [PaymentWorkflowEvent.PAYMENT_SETUP_STARTED]: PaymentEventBaseMetadata;
    // intentTrigger distinguishes a user-driven payable selection from the portal's auto-selection on
    // load (and the URL-driven rehydration on the review-and-pay page) — see PaymentIntentTrigger.
    [PaymentWorkflowEvent.PAYMENT_PAYABLES_SELECTED]: PaymentEventBaseMetadata & {
      subtotal: number;
      payableCount: number;
      intentTrigger: PaymentIntentTrigger;
    };
    [PaymentWorkflowEvent.PAYMENT_NON_INVOICE_STARTED]: PaymentEventBaseMetadata;
    // Base envelope only: no method is selected yet at unlock time (paymentMethodType is undefined).
    [PaymentWorkflowEvent.PAYMENT_METHODS_UNLOCKED]: PaymentEventBaseMetadata;
    // intentTrigger distinguishes a user-driven method selection from the prepopulated method
    // auto-selected on load — see PaymentIntentTrigger.
    [PaymentWorkflowEvent.PAYMENT_METHOD_SELECTED]: PaymentEventBaseMetadata & { intentTrigger: PaymentIntentTrigger };
    // confirmationMade — the payment info step was confirmed. paymentMethodAssigned — a method was
    // captured/assigned. prepaymentCharged — the prepayment charged now (`false` ⇒ deferred until all
    // signatures are collected ⇒ engagement entered the Waiting state). No PII / free text.
    [PaymentWorkflowEvent.PAYMENT_INFO_CONFIRMED]: PaymentEventBaseMetadata & {
      confirmationMade: boolean;
      paymentMethodAssigned: boolean;
      prepaymentCharged: boolean;
    };
    [PaymentWorkflowEvent.PAYMENT_VALIDATION_FAILED]: PaymentEventBaseMetadata & FailureContext;
    // chargeIndex 0 = the initial payables / pay-without-invoice charge; 1..N = the Nth add-on charge. When credits fully cover
    // the payables, the initial charge is skipped and only add-on events (chargeIndex >= 1) fire. Every charge carries its own
    // subtotal + processing/international fees (add-ons incur fees too). creditAmount and lateFeeAmount are batch-level deductions
    // that only apply to the initial — add-ons don't accept credits and run with `ignoreLateFees: true`.
    [PaymentWorkflowEvent.PAYMENT_CHARGE_SUBMITTED]: PaymentEventBaseMetadata & {
      amount: number;
      chargeIndex: number;
      subtotal: number;
      processingFeeAmount: number;
      internationalFeeAmount: number;
      // Initial-only (chargeIndex === 0).
      creditAmount?: number;
      lateFeeAmount?: number;
      // ACH only: true when the bank account used was linked via Stripe Financial Connections
      // (connected account / instant verification) rather than entered manually. Sourced from the
      // selected payment method's `isConnectedAccount` — for saved methods this originates from the
      // backend payment-methods V5 response (ART-8093). Omitted for card / ACSS / BACS.
      isConnectedAccount?: boolean;
    };
    [PaymentWorkflowEvent.PAYMENT_CHARGE_SUCCEEDED]: PaymentEventBaseMetadata & { amount: number; chargeIndex: number; operationDurationMs: number };
    [PaymentWorkflowEvent.PAYMENT_CHARGE_FAILED]: PaymentEventBaseMetadata & FailureContext & {
      chargeIndex: number;
      operationDurationMs: number;
      // Stripe-sourced when present. Best-effort: undefined if the thrown error doesn't expose them.
      stripeDeclineCode?: string;
      stripeErrorCode?: string;
      // Same shape as PAYMENT_CHARGE_SUBMITTED so a failed charge can be sliced by size / fees / credits
      // without joining back to the prior submitted event.
      amount?: number;
      subtotal?: number;
      processingFeeAmount?: number;
      internationalFeeAmount?: number;
      // Initial charge-submit only (chargeIndex === 0).
      creditAmount?: number;
      lateFeeAmount?: number;
    };
    [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_SUBMITTED]: PaymentEventBaseMetadata;
    [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_COMPLETE]: PaymentEventBaseMetadata & { operationDurationMs: number };
    [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_FAILED]: PaymentEventBaseMetadata & FailureContext & { operationDurationMs: number };
    [PaymentWorkflowEvent.PAYMENT_CANCELLED]: PaymentEventBaseMetadata & {
      cancellationReason: string;
      cancellationSource: PaymentCancellationSource;
    };
    // The two late-fee guard events fire at fee-LIST load, before any payment exists, so they
    // deliberately do not carry PaymentEventBaseMetadata. SUPPRESSED = the read model offered a
    // fee whose active allocations already cover it (the paid-fee-re-offered incident signature,
    // observed live); each firing is one client who would otherwise have been shown a paid fee.
    [PaymentWorkflowEvent.PAYMENT_LATE_FEES_SUPPRESSED]: {
      suppressedFeeCount: number;
      totalFeeCount: number;
      suppressedTotal: number;
      suppressedFeeIds: string;
    };
    // CHECK_FAILED = the allocation cross-check could not complete (lookup error, or a fee the
    // allocation query didn't return). The portal fails CLOSED — every fee is hidden — so this
    // event is the only visibility that fees were withheld from a client.
    [PaymentWorkflowEvent.PAYMENT_LATE_FEE_ALLOCATION_CHECK_FAILED]: FailureContext & {
      totalFeeCount: number;
      checkOutcome: 'lookup-error' | 'incomplete-data';
    };
    // The credit guard mirrors the late-fee pair and fires at credit-LIST load, before any
    // payment exists. SUPPRESSED = the read model offered a credit whose active allocations
    // already spend it; each firing is one client who would otherwise have been shown a spent
    // credit — and, because the server has no credit-side allocation cap, could have applied it
    // a second time against a different invoice.
    [PaymentWorkflowEvent.PAYMENT_CREDITS_SUPPRESSED]: {
      suppressedCreditCount: number;
      totalCreditCount: number;
      suppressedTotal: number;
      suppressedCreditIds: string;
    };
    // CHECK_FAILED = the allocation cross-check could not complete (lookup error, or a credit the
    // allocation query didn't return). The portal fails CLOSED — every credit is withheld — so
    // this event is the only visibility that a client was asked to pay without their credits.
    [PaymentWorkflowEvent.PAYMENT_CREDIT_ALLOCATION_CHECK_FAILED]: FailureContext & {
      totalCreditCount: number;
      checkOutcome: 'lookup-error' | 'incomplete-data';
    };
  }
>;

export const PaymentEventEventTypeMap: WorkflowEventEventTypeMap<PaymentWorkflowEvent> = {
  ...generateEventTypeMapDefaults(PaymentWorkflowEvent, EventType.USER),
  [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_SUBMITTED]: EventType.SYSTEM,
  [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_COMPLETE]: EventType.SYSTEM,
  [PaymentWorkflowEvent.PAYMENT_CHARGE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentWorkflowEvent.PAYMENT_VALIDATION_FAILED]: EventType.ERROR,
  [PaymentWorkflowEvent.PAYMENT_CHARGE_FAILED]: EventType.ERROR,
  [PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_FAILED]: EventType.ERROR,
  [PaymentWorkflowEvent.PAYMENT_LATE_FEES_SUPPRESSED]: EventType.SYSTEM,
  [PaymentWorkflowEvent.PAYMENT_LATE_FEE_ALLOCATION_CHECK_FAILED]: EventType.ERROR,
  [PaymentWorkflowEvent.PAYMENT_CREDITS_SUPPRESSED]: EventType.SYSTEM,
  [PaymentWorkflowEvent.PAYMENT_CREDIT_ALLOCATION_CHECK_FAILED]: EventType.ERROR,
};

/**
 * Source of a PAYMENT_CANCELLED event:
 *  - '3ds-user-cancel' — user dismissed the 3D Secure dialog (PaymentActionRequirementDialogComponent → "Cancel").
 *  - '3ds-auth-failed' — Stripe rejected the 3D Secure challenge during confirm*Payment.
 *  - 'system' — internal cancellation path not driven by the user (e.g., batch teardown).
 *  - 'unknown' — fallback when the source could not be determined.
 */
export const PaymentCancellationSource = {
  THREE_DS_USER_CANCEL: '3ds-user-cancel',
  THREE_DS_AUTH_FAILED: '3ds-auth-failed',
  SYSTEM: 'system',
  UNKNOWN: 'unknown',
} as const;
export type PaymentCancellationSource = (typeof PaymentCancellationSource)[keyof typeof PaymentCancellationSource];

/**
 * `name` attached to the synthetic Error emitted when the user dismisses the 3D Secure dialog
 * (no backing native cancel flow exists, so we have to infer the source from this marker). The
 * cancel-dispatch sites compare against this constant to tag PAYMENT_CANCELLED with
 * `cancellationSource: '3ds-user-cancel'`; everything else falls through to '3ds-auth-failed'.
 */
export const THREE_DS_USER_CANCEL_ERROR_NAME = '3D Auth Canceled' as const;
